import * as Sentry from '@sentry/react';

export default function sentryInit(environment: string): void {
    if (process.env.NODE_ENV === 'development') {
        console.log('disabling sentry in development');
        return;
    }

    // for compiled mode
    if (window.location.hostname === 'localhost') {
        console.log('disabling sentry on localhost');
        return;
    }

    Sentry.init({
        dsn: 'https://caa20c4d2f3d8b850a9480015526650c@o1021052.ingest.sentry.io/4506300144484352',
        integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
        // Performance Monitoring
        tracesSampleRate: 0.1,
        environment,
        replaysSessionSampleRate: 0.1,
        ignoreErrors: [/invalid JSON from Parse/i], // TODO: Once we migrate to AWS, remove me
    });
}

export function setSentryAccount(accountName: string): void {
    Sentry.setTag('account', accountName);
}

export function setSentryUser(userId: string, userEmail: string): void {
    Sentry.setUser({
        id: userId,
        email: userEmail,
    });
}

const SENTRY_TIMEOUT_MS = 1000;
const SENTRY_FAILURE_THRESHOLD = 3;
const SENTRY_FAILURE_TIMEOUT_MS = 30_000;

let sentryFailures = 0;
let sentryDisabledUntil = 0;

export async function captureSentryException(
    e: Error | unknown,
    additionalTags?: Record<string, string>
): Promise<void> {
    console.error('Captured Exception e', e);

    // Circuit breaker: skip Sentry if it's been failing
    if (Date.now() < sentryDisabledUntil) {
        console.warn('Sentry disabled due to failures, skipping');
        return;
    }

    // Fire and forget with timeout
    Promise.race([
        Sentry.captureException(e, {
            tags: {
                href: window.location?.href,
                ...(additionalTags || {}),
            },
        }),
        new Promise((_, reject) =>
            setTimeout(
                () => reject(new Error('Sentry timeout')),
                SENTRY_TIMEOUT_MS
            )
        ),
    ])
        .then(() => {
            sentryFailures = 0;
        })
        .catch((err) => {
            sentryFailures++;

            if (sentryFailures >= SENTRY_FAILURE_THRESHOLD) {
                sentryDisabledUntil = Date.now() + SENTRY_FAILURE_TIMEOUT_MS;
                console.error(
                    `Sentry failed ${sentryFailures} times, disabling for ${
                        SENTRY_FAILURE_TIMEOUT_MS / 1000
                    }s`
                );
                sentryFailures = 0;
            } else {
                console.error('Sentry error:', err.message);
            }
        });

    // Immediately resolve so that this call isn't blocking
    return Promise.resolve();
}
